/* Règles de la page résultats (« 02 - Résultats de recherche ») : filtrage,
   tri et pagination côté navigateur. L'API ne filtre ni par destination, ni
   par durée, ni ne trie : on récupère les publications une fois, puis tout
   se calcule ici. */

export const PAGE_SIZE = 9;

export const SORTS = ["relevance", "price-asc", "price-desc", "duration-asc", "duration-desc"];

/* Tranches de durée de la maquette (en jours). */
export const DURATIONS = [
  { value: "short", min: 0, max: 5 },
  { value: "medium", min: 6, max: 8 },
  { value: "long", min: 9, max: 11 },
  { value: "xl", min: 12, max: Infinity },
];

const normalize = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();

export const resolveLocations = (item) => {
  const raw = Array.isArray(item?.locations)
    ? item.locations
    : Array.isArray(item?.cities)
    ? item.cities
    : Array.isArray(item?.tripProgramDetails)
    ? item.tripProgramDetails.map((step) => step?.city || step?.country)
    : [item?.city];

  const seen = new Set();
  return (raw || []).filter((entry) => {
    const key = normalize(entry);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const matchesDestination = (item, destination) => {
  const wanted = normalize(destination);
  if (!wanted) return true;

  return [...resolveLocations(item), item?.city, item?.country, item?.title, item?.smallDescription]
    .map(normalize)
    .filter(Boolean)
    .some((entry) => entry.includes(wanted));
};

/* « AAAA-MM-JJ » lu en date locale (new Date("2026-10-12") serait en UTC). */
export const parseDate = (value) => {
  if (!value) return null;
  const parts = String(value).slice(0, 10).split("-").map(Number);
  if (parts.length === 3 && parts.every(Boolean)) {
    return new Date(parts[0], parts[1] - 1, parts[2]);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/* Un voyage correspond s'il chevauche la période demandée. */
export const matchesDateRange = (item, startDate, endDate) => {
  const from = parseDate(startDate);
  const to = parseDate(endDate);
  if (!from && !to) return true;

  const tripStart = parseDate(item?.startDate);
  const tripEnd = parseDate(item?.endDate) || tripStart;
  if (!tripStart && !tripEnd) return false;

  if (from && (tripEnd || tripStart) < from) return false;
  if (to && (tripStart || tripEnd) > to) return false;
  return true;
};

export const getNbDays = (item) => {
  const nights = Number(item?.nbNights);
  if (Number.isFinite(nights) && nights > 0) return nights + 1;
  const start = parseDate(item?.startDate);
  const end = parseDate(item?.endDate);
  if (!start || !end) return null;
  return Math.max(1, Math.round((end - start) / 86400000) + 1);
};

export const getPrice = (item) => {
  const value = Number(item?.price?.value);
  return Number.isFinite(value) ? value : null;
};

export const matchesDurations = (item, durations) => {
  if (!durations.length) return true;
  const days = getNbDays(item);
  if (days === null) return false;
  return DURATIONS.some((d) => durations.includes(d.value) && days >= d.min && days <= d.max);
};

export const matchesMaxPrice = (item, maxPrice) => {
  if (!maxPrice) return true;
  const price = getPrice(item);
  return price === null || price <= maxPrice;
};

export const sortItems = (items, sort) => {
  if (!sort || sort === "relevance") return items;
  const [key, direction] = sort.split("-");
  const read = key === "price" ? getPrice : getNbDays;
  const factor = direction === "desc" ? -1 : 1;
  // Les voyages sans prix ou sans durée passent toujours en fin de liste.
  return [...items].sort((a, b) => {
    const va = read(a);
    const vb = read(b);
    if (va === null && vb === null) return 0;
    if (va === null) return 1;
    if (vb === null) return -1;
    return (va - vb) * factor;
  });
};

/* Bornes du curseur de budget, arrondies à la centaine. */
export const getPriceBounds = (items) => {
  const prices = items.map(getPrice).filter((p) => p !== null && p > 0);
  if (!prices.length) return null;
  return {
    min: Math.floor(Math.min(...prices) / 100) * 100,
    max: Math.ceil(Math.max(...prices) / 100) * 100,
    currency: items.find((i) => i?.price?.currency)?.price?.currency || "",
  };
};

/* Pays distincts des voyages disponibles : suggestions de l'état vide. */
export const getCountrySuggestions = (items, limit = 5) => {
  const seen = new Map();
  items.forEach((item) => {
    String(item?.country || "")
      .split("·")
      .map((c) => c.trim())
      .filter(Boolean)
      .forEach((country) => {
        const key = normalize(country);
        if (!seen.has(key)) seen.set(key, country);
      });
  });
  return [...seen.values()].slice(0, limit);
};

export const formatAmount = (value, currency, locale = "fr-FR") =>
  `${Number(value).toLocaleString(locale)}${currency ? ` ${currency}` : ""}`;
